import DefaultGradeKeys from '@opsbase/lib-grading/DefaultGradeKeys';
import getGradeFromScoreAndCalculator from '@opsbase/lib-grading/getGradeFromScoreAndCalculator';
import GradingRuleType from '@opsbase/lib-grading/GradingRuleType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import GradeChip from '@opsbase/web-chips/GradeChip';
import useCss from '@opsbase/web-css';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import GrabIcon from '@opsbase/web-icons/icons/GrabIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import GradePicker from '@opsbase/web-pickers/GradePicker';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Button from '@opsbase/web-ui/Button';
import IconButton from '@opsbase/web-ui/IconButton';
import MenuItem from '@opsbase/web-ui/MenuItem';
import Select from '@opsbase/web-ui/Select';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import * as uuid from 'uuid';
const labels = {
    [GradingRuleType.SCORELT]: () => i18n.lc('less-than'),
    [GradingRuleType.SCOREGTE]: () => i18n.lc('greater-than'),
    [GradingRuleType.SCOREBETWEEN]: () => i18n.lc('between'),
};
const makeMyClasses = () => ({
    rule: {
        border: `1px solid ${Palette.LineGrey.main}`,
        borderRadius: 8,
        padding: 8,
        backgroundColor: 'white',
        margin: '4px 0',
    },
    testPane: {
        maxHeight: 48,
        backgroundColor: 'white',
        borderTop: `1px solid ${Palette.BorderGrey.main}`,
    },
});
export function GradingCalculatorDialog(props) {
    const { value, onChange, onCancel } = props;
    const { raiseError } = useNotificationActions();
    const [gradingCalculator, setGradingCalculator] = useState(value);
    const [testScore, setTestScore] = useState(50);
    const [rules, setRules] = useState(value.rules.map(r => ({
        id: uuid.v4(),
        ...r,
    })));
    const myCss = useCss(makeMyClasses);
    const InputProps = useMemo(() => ({
        style: {
            width: 90,
            padding: '4px !important',
        },
        endAdornment: (React.createElement(Text, { variant: "small", color: "lightgrey" }, "%")),
    }), []);
    const inputProps = useMemo(() => ({
        min: 0,
        max: 100,
        style: {
            textAlign: 'right',
            padding: '4px !important',
        },
    }), []);
    const handleChangeTitle = useCallback(title => {
        setGradingCalculator({ ...gradingCalculator, title });
    }, [gradingCalculator]);
    const handleChangeFailover = useCallback(failoverGrade => {
        setGradingCalculator({ ...gradingCalculator, failoverGrade });
    }, [gradingCalculator]);
    const handleChangeImmediate = useCallback(immediateFailGrade => {
        setGradingCalculator({ ...gradingCalculator, immediateFailGrade });
    }, [gradingCalculator]);
    const handleChangeRule = useCallback((idx, field) => v => {
        if ((field === 'scoreLt' || field === 'scoreGte') && v < 0)
            v = 0;
        if ((field === 'scoreLt' || field === 'scoreGte') && v > 100)
            v = 100;
        setRules(rules.map((r, i) => {
            if (i === idx) {
                return { ...r, [field]: v };
            }
            else {
                return { ...r };
            }
        }));
    }, [rules]);
    const handleDeleteRule = useCallforward(idx => () => {
        setRules(rules.filter((r, i) => i !== idx));
    }, [rules]);
    const handleClickAdd = useCallback(() => {
        setRules([
            ...rules,
            {
                id: uuid.v4(),
                type: GradingRuleType.SCOREGTE,
                scoreGte: 0,
                scoreLt: 0,
                grade: DefaultGradeKeys.UNDETERMINED,
            },
        ]);
    }, [rules]);
    const handleChangeTestScore = useCallback(v => {
        let s;
        try {
            s = parseFloat(v);
        }
        catch (_a) { }
        if (s < 0)
            s = 0;
        if (s > 100)
            s = 100;
        if (isNaN(s)) {
            s = 0;
        }
        setTestScore(s);
    }, []);
    const handleClickCancel = useCallback(() => onCancel(), [onCancel]);
    const handleClickOk = useCallback(() => {
        for (let i = 0; i < rules.length; i++) {
            for (let j = i + 1; j < rules.length; j++) {
                if (checkOverlap(rules[i], rules[j])) {
                    return raiseError(i18n.t('overlap-rules'));
                }
            }
        }
        onChange({ ...gradingCalculator, rules });
    }, [onChange, gradingCalculator, rules, raiseError]);
    const testGrade = getGradeFromScoreAndCalculator(testScore, { ...gradingCalculator, rules });
    return (React.createElement(Dialog, { editableTitle: true, title: gradingCalculator.title, size: DialogSize.X1000_Y500, open: true, closable: false, padded: false, scroll: false, onChangeTitle: handleChangeTitle, buttonsLeft: React.createElement(Button, { color: Palette.Platinum, onClick: handleClickCancel, label: i18n.t('cancel') }), buttonsRight: React.createElement(Horizontal, { gutter: true },
            React.createElement(Button, { background: true, color: Palette.GoodOldBlue, onClick: handleClickAdd, label: i18n.t('add-rule') }),
            React.createElement(Button, { color: Palette.positive, onClick: handleClickOk, label: gradingCalculator.new ? i18n.t('create') : i18n.t('save') })) },
        React.createElement(Vertical, null,
            React.createElement(Pane, { grow: true, padding: true, scroll: true },
                React.createElement(Horizontal, { style: { marginLeft: 32, marginRight: 32 }, classes: { root: myCss.rule }, centered: true, gutter: true, justify: "flex-start" },
                    React.createElement(Text, null, i18n.t('if-immediate-answer')),
                    React.createElement(GradePicker, { noMargin: true, white: true, value: gradingCalculator.immediateFailGrade, onChange: handleChangeImmediate })),
                React.createElement(ReactSortable, { id: 'editableList', handle: ".dragHandle", animation: 100, direction: "vertical", easing: "cubic-bezier(1, 0, 0, 1)", list: rules, setList: setRules }, rules.map((rule, idx) => {
                    const type = rule.type;
                    const showLt = type === GradingRuleType.SCOREBETWEEN ||
                        type == GradingRuleType.SCORELT;
                    const showGte = type === GradingRuleType.SCOREBETWEEN ||
                        type == GradingRuleType.SCOREGTE;
                    return (React.createElement(Horizontal, { centered: true, gutter: true, key: rule.id },
                        React.createElement(IconButton, { icon: React.createElement(GrabIcon, { color: Palette.LavendarGrey, className: "dragHandle" }) }),
                        React.createElement(Horizontal, { classes: { root: myCss.rule }, justify: "flex-start", centered: true, gutter: true },
                            React.createElement(Text, null, i18n.t('if-score-is')),
                            React.createElement(Select, { value: type, noBorder: true, onChange: handleChangeRule(idx, 'type') }, Object.values(GradingRuleType).map(rt => (React.createElement(MenuItem, { key: rt, value: rt }, labels[rt]())))),
                            !!showGte && (React.createElement(TextFieldWithStateAndBlur, { noMargin: true, InputProps: {
                                    ...InputProps,
                                    startAdornment: (React.createElement(Text, { variant: "small", color: "lightgrey" }, ">=")),
                                }, inputProps: inputProps, type: "number", value: rule.scoreGte || 0, onChange: handleChangeRule(idx, 'scoreGte') })),
                            !!showGte && !!showLt && React.createElement(Text, null, "and"),
                            !!showLt && (React.createElement(TextFieldWithStateAndBlur, { noMargin: true, InputProps: {
                                    ...InputProps,
                                    startAdornment: (React.createElement(Text, { variant: "small", color: "lightgrey" }, "<")),
                                }, inputProps: inputProps, type: "number", value: rule.scoreLt || 0, onChange: handleChangeRule(idx, 'scoreLt') })),
                            React.createElement(Text, null, i18n.t('then-grade-is')),
                            React.createElement(GradePicker, { noMargin: true, white: true, value: rule.grade, onChange: handleChangeRule(idx, 'grade') })),
                        React.createElement(IconButton, { icon: React.createElement(DeleteIcon, { color: Palette.LavendarGrey }), onClick: handleDeleteRule(idx) })));
                })),
                React.createElement(Horizontal, { style: { marginLeft: 32, marginRight: 32 }, classes: { root: myCss.rule }, centered: true, gutter: true, justify: "flex-start" },
                    React.createElement(Text, null, i18n.t('otherwise-grade-is')),
                    React.createElement(GradePicker, { noMargin: true, white: true, value: gradingCalculator.failoverGrade, onChange: handleChangeFailover }))),
            React.createElement(Horizontal, { justify: "center", gutter: true, centered: true, padding: true, classes: { root: myCss.testPane } },
                React.createElement(Text, null, i18n.t('a-score-of')),
                React.createElement(TextFieldWithStateAndBlur, { noMargin: true, InputProps: InputProps, inputProps: inputProps, type: "number", value: testScore, onChange: handleChangeTestScore }),
                React.createElement(Text, null, i18n.t('would-grade-as')),
                React.createElement(GradeChip, { showPass: true, grade: testGrade })))));
}
export default GradingCalculatorDialog;
function checkOverlap(score1, score2) {
    return ((score1.scoreLt || 99) - (score2.scoreGte || 0) > 0 &&
        (score2.scoreLt || 99) - (score1.scoreGte || 0) > 0);
}
